import { Directive, ElementRef, HostListener, Renderer2, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Formats a money input for display while keeping a plain number in the form model.
 *
 *  - Model value stays a `number | null` (e.g. 25000.5).
 *  - While the field is blurred it shows grouped, 2-decimal text: `25,000.50`.
 *  - On focus it shows the raw editable number: `25000.5`.
 *  - Typing is restricted to digits and a single decimal point (max 2 places).
 *
 * Pair it with a `<span matPrefix>$</span>` in the template for the currency symbol.
 *
 * Usage: <input type="text" inputmode="decimal" appCurrencyInput formControlName="flatRateAmount">
 */
@Directive({
  selector: 'input[appCurrencyInput]',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CurrencyInputDirective),
    multi: true
  }]
})
export class CurrencyInputDirective implements ControlValueAccessor {
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};
  private value: number | null = null;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2
  ) {}

  writeValue(value: any): void {
    this.value = this.toNumber(value);
    this.setInputValue(this.format(this.value));
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }

  @HostListener('input', ['$event.target.value'])
  onInput(raw: string): void {
    let cleaned = raw.replace(/[^0-9.]/g, '');

    // keep only the first decimal point
    const firstDot = cleaned.indexOf('.');
    if (firstDot !== -1) {
      cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
    }

    // strip a leading zero that isn't "0." (prevents "025000")
    cleaned = cleaned.replace(/^0+(?=\d)/, '');

    // limit to 2 decimal places
    const [whole, decimals] = cleaned.split('.');
    if (decimals !== undefined) {
      cleaned = whole + '.' + decimals.slice(0, 2);
    }

    if (cleaned !== raw) {
      this.setInputValue(cleaned);
    }

    this.value = cleaned === '' || cleaned === '.' ? null : parseFloat(cleaned);
    this.onChange(this.value);
  }

  @HostListener('focus')
  onFocus(): void {
    this.setInputValue(this.value === null || isNaN(this.value) ? '' : String(this.value));
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
    this.setInputValue(this.format(this.value));
  }

  private setInputValue(text: string): void {
    this.renderer.setProperty(this.el.nativeElement, 'value', text);
  }

  private toNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const n = typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/[^0-9.\-]/g, ''));
    return isNaN(n) ? null : n;
  }

  private format(value: number | null): string {
    if (value === null || isNaN(value)) {
      return '';
    }
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
}
